import { Client, Databases, Functions } from 'appwrite';
import { APP_CONFIG } from '../config/app';

// --- Appwrite Client Initialization ---
const client = new Client()
    .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
    .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

export const databases = new Databases(client); // Still needed if any part of app uses it directly (e.g. for non-proxied calls, though ideally not)
export const functions = new Functions(client); // Needed for createExecution

// --- Security & Concurrency State ---
// const registrationInProgress = new Set(); // REMOVED - As createUserDocument is removed

// --- Internal Helper Functions ---
// All internal helper functions that directly interacted with Appwrite using specific IDs
// (_getUserDocumentByTelegramId, checkReferralRateLimit) have been removed
// as this logic is now backend or obsolete.

// --- Exported API Functions ---

/**
 * Generic function to call our backend Appwrite proxy.
 * @param {string} action The action to perform on the backend.
 * @param {object} payload The data to send for the action.
 * @returns {Promise<object>} The response from the backend function.
 */
const callAppwriteProxy = async (action, payload) => {
    if (!process.env.REACT_APP_VERIFY_FUNCTION_ID) {
        console.error('REACT_APP_VERIFY_FUNCTION_ID is not set.');
        throw new Error('Server function ID is not configured.');
    }
    try {
        const result = await functions.createExecution(
            process.env.REACT_APP_VERIFY_FUNCTION_ID,
            JSON.stringify({ payload, action }), // Send payload as a nested object
            false, // sync
            '/',   // path
            'POST', // method
            { 'Content-Type': 'application/json' }
        );

        if (result.status === 'completed') {
            const response = JSON.parse(result.responseBody);
            if (!response.success) {
                console.error(`Appwrite proxy action '${action}' failed (server):`, response.message);
                throw new Error(response.message || `Action ${action} failed.`);
            }
            return response; // Contains { success: true, ...otherData }
        } else {
            console.error(`Appwrite proxy action '${action}' function execution failed:`, result.stderr);
            throw new Error(`Function execution for ${action} failed: ${result.stderr || 'Unknown error'}`);
        }
    } catch (error) {
        console.error(`Error calling Appwrite proxy action '${action}':`, error);
        // Keep the original error message if it's more specific
        throw new Error(error.message || `Failed to execute action ${action}.`);
    }
};

/**
 * Initializes application data by fetching user data, mining progress, sponsors, etc.,
 * and handles user creation if the user doesn't exist.
 * @param {object} telegramUser - The user object from Telegram WebApp (must contain at least `id`, `username`).
 * @param {string|null} referredByCode - The referral code (Telegram ID of referrer) if any.
 * @returns {Promise<object>} Combined data for app initialization or an error object.
 */
export const initializeAppData = async (telegramUser, referredByCode = null) => {
    if (!telegramUser || !telegramUser.id) {
        console.error('initializeAppData: telegramUser or telegramUser.id is missing.');
        return { success: false, error: 'Valid Telegram user data is required for initialization.' };
    }

    try {
        const payload = {
            user: {
                id: telegramUser.id.toString(),
                username: telegramUser.username || '',
                // Include other relevant fields from telegramUser if needed by backend for creation
                // e.g., first_name: telegramUser.first_name
            },
            referredBy: referredByCode ? referredByCode.toString() : null
        };
        const response = await callAppwriteProxy('initializeApp', payload);
        return response; // Backend returns a comprehensive object
    } catch (error) {
        console.error('Error initializing app data via proxy:', error);
        return { success: false, error: error.message || 'Failed to initialize application data.' };
    }
};


/**
 * Checks if a user document already exists.
 */
// export const checkUserExists = async (userId) => { // REMOVED - Functionality handled by initializeApp
//     if (!userId) {
//         console.error('Cannot check user: userId is missing');
//         return false;
//     }
//     try {
//         const response = await callAppwriteProxy('checkUserExists', { userId: userId.toString() });
//         return response.exists;
//     } catch (error) {
//         console.error('checkUserExists failed via proxy:', error);
//         throw error;
//     }
// };

/**
 * Creates a new user document, handles referrals, and sets initial data.
 */
// export const createUserDocument = async (user, referredBy = null) => { // REMOVED - Functionality handled by initializeApp
//     if (!user?.id) {
//         console.error('Cannot create document: user or user.id is missing', user);
//         return null;
//     }
//     const userId = user.id.toString();
//     if (registrationInProgress.has(userId)) {
//         console.warn(`Registration for ${userId} already in progress.`);
//         return null;
//     }
//     registrationInProgress.add(userId);
//     try {
//         const payload = {
//             user: {
//                 id: userId,
//                 username: user.username || '',
//             },
//             referredBy: referredBy ? referredBy.toString() : null
//         };
//         const response = await callAppwriteProxy('createUserDocument', payload);
//         return response;
//     } catch (error) {
//         console.error('Error creating user document via proxy:', error);
//         return { success: false, error: error.message || 'Failed to create user document.' };
//     } finally {
//         registrationInProgress.delete(userId);
//     }
// };

/**
 * Updates mining progress using server-side calculations
 */
export const updateMiningProgress = async (userId) => {
    // userId is Telegram ID
    if (!userId) {
        console.error('User ID is required to update mining progress.');
        return { success: false, error: 'User ID is required.' };
    }
    try {
        const response = await callAppwriteProxy('updateMiningProgress', { userId: userId.toString() });
        // The backend action 'updateMiningProgress' already returns the necessary fields:
        // { success: true, minedAmount, elapsedSeconds, minedWhileAway, minerPower, lastMiningTime, offlineMining }
        return response;
    } catch (error) {
        console.error('Error updating mining progress via proxy:', error);
        return { success: false, error: error.message || 'Failed to update mining progress.' };
    }
};

/**
 * Claims mined coins by calling the backend function.
 * The backend function will determine the actual amount based on server state.
 */
export const claimMinedSecondaryCoin = async (telegramId) => {
    // telegramId is the user's Telegram ID.
    if (!telegramId) {
        console.error('Telegram ID is required to claim mined coins.');
        return { success: false, error: 'Telegram ID is required.' };
    }

    try {
        // Backend action 'claimMinedCoins' expects telegramId via payload.userId
        const response = await callAppwriteProxy('claimMinedCoins', { userId: telegramId.toString() });
        // The backend action already returns the necessary fields:
        // { success: true, claimedAmount, newBalance }
        // or { success: false, message }
        return response;
    } catch (error) {
        console.error(`Error claiming ${APP_CONFIG.SECONDARY_COIN_NAME} via proxy:`, error);
        return { success: false, error: error.message || `Failed to process claim for ${APP_CONFIG.SECONDARY_COIN_NAME}.` };
    }
};

/**
 * Gets and maps all relevant data for a user.
 */
export const getUserData = async (userId) => {
    // userId is Telegram ID
    if (!userId) {
        console.error('getUserData: userId is missing');
        return null; // Or throw
    }
    try {
        const response = await callAppwriteProxy('getUserData', { userId: userId.toString() });
        // The backend now returns the mapped data directly in response.userData
        return response.userData;
    } catch (error) {
        console.error('getUserData failed via proxy:', error);
        return null; // Or re-throw
    }
};

/**
 * Calculates and updates rewards for offline mining.
 * This logic is now part of 'updateMiningProgress' on the backend.
 * The client might not need a separate 'calculateOfflineMining' if 'updateMiningProgress'
 * is called upon app load and returns all necessary info.
 * For now, let's assume 'updateMiningProgress' handles this.
 * If specific offline calculation display is needed before an update, this might need to be a separate call.
 */
// export const calculateOfflineMining = async (userId) => { // REMOVED - Deprecated
//     console.warn("calculateOfflineMining is deprecated. Use updateMiningProgress which returns offline mining data.");
//     return null;
// };

/**
 * Updates a user's miner power.
 */
export const updateMinerPower = async (userId, newPower) => {
    // userId is Telegram ID
    if (!userId || typeof newPower !== 'number' || newPower <= 0) {
        console.error('Invalid parameters for updateMinerPower');
        return { success: false, error: 'Invalid parameters' };
    }
    try {
        // Standardized: pass newPower directly in the payload object
        const response = await callAppwriteProxy('updateMinerPower', { userId: userId.toString(), newPower: newPower });
        return { success: true, newMinerPower: response.newMinerPower }; // Backend should return newMinerPower
    } catch (error) {
        console.error('updateMinerPower failed via proxy:', error);
        return { success: false, error: error.message || 'Failed to update miner power' };
    }
};

/**
 * Updates the last mining time, typically when the user leaves the app.
 * This is best handled by the backend during operations like `updateMiningProgress` or a dedicated `recordAppExit`.
 * Forcing a client call on app exit (e.g. beforeunload) is unreliable.
 * The backend `updateMiningProgress` already updates `last_mining_time`.
 */
// export const updateLastMiningTime = async (userId) => { // REMOVED - Redundant
//     console.warn("updateLastMiningTime may be redundant if updateMiningProgress is used effectively.");
//     return true;
// };

/**
 * Gives a referral bonus to the referrer.
 * This logic should be entirely backend, likely triggered during `createUserDocument` if a valid referrer exists.
 */
// export const giveReferralBonus = async (referrerId) => { // REMOVED - Deprecated
//     console.warn("giveReferralBonus is deprecated on client-side. Backend handles this during user creation.");
//     return true;
// };

/**
 * Gets a user's referral statistics.
 */
export const getReferralStats = async (userId) => {
    // userId is Telegram ID
    if (!userId) {
        console.error('getReferralStats: userId is missing');
        return null;
    }
    try {
        const response = await callAppwriteProxy('getReferralStats', { userId: userId.toString() });
        // Expect backend to return: { referralCode, referredCount, primaryCoinBalance }
        return response.stats;
    } catch (error) {
        console.error('getReferralStats failed via proxy:', error);
        return null;
    }
};

/**
 * Gets a user document by their referral code.
 */
export const getUserByReferralCode = async (referralCode) => {
    if (!referralCode) {
        console.error('getUserByReferralCode: referralCode is missing');
        return null;
    }
    try {
        const response = await callAppwriteProxy('getUserByReferralCode', { payload: { referralCode } });
        // Expect backend to return { userDocument } or null if not found
        return response.userDocument;
    } catch (error) {
        console.error('getUserByReferralCode failed via proxy:', error);
        return null;
    }
};

/**
 * Adds a specified amount to a user's primary coin balance.
 */
export const addPrimaryCoinBalance = async (userId, amount) => {
    // userId is Telegram ID
    if (!userId || typeof amount !== 'number' || amount <= 0) {
        return { success: false, error: 'Invalid request parameters for addPrimaryCoinBalance' };
    }
    try {
        // Standardized: pass amount directly in the payload object
        const response = await callAppwriteProxy('addPrimaryCoinBalance', { userId: userId.toString(), amount: amount });
        return { success: true, newBalance: response.newBalance };
    } catch (error) {
        console.error('addPrimaryCoinBalance failed via proxy:', error);
        return { success: false, error: error.message || 'Failed to update balance' };
    }
};

/**
 * Creates a new deposit record in the database.
 */
export const createDepositRecord = async (transactionId, userId, coinAmount, tonAmount) => {
    // userId is Telegram ID
    if (!transactionId || !userId || typeof coinAmount !== 'number' || typeof tonAmount !== 'number') {
        console.error('Invalid parameters for createDepositRecord');
        return null;
    }
    try {
        // Standardized: pass arguments directly in the payload object
        const response = await callAppwriteProxy('createDepositRecord', {
            transactionId,
            userId: userId.toString(),
            coinAmount,
            tonAmount
        });
        return response.document; // Expect backend to return the created document
    } catch (error) {
        console.error('createDepositRecord failed via proxy:', error);
        return null;
    }
};

/**
 * Updates the status of an existing deposit record.
 */
export const updateDepositRecordStatus = async (transactionId, status, errorMessage = null) => {
    if (!transactionId || !status) {
        console.error('Invalid parameters for updateDepositRecordStatus');
        return null;
    }
    try {
        // Standardized: pass arguments directly in the payload object
        const response = await callAppwriteProxy('updateDepositRecordStatus', {
            transactionId,
            status,
            errorMessage
        });
        return response.document; // Expect backend to return the updated document
    } catch (error) {
        console.error('updateDepositRecordStatus failed via proxy:', error);
        return null;
    }
};

/**
 * Fetches all users referred by a specific user ID.
 */
/**
 * Rents a new miner, deducting cost and increasing power.
 */
export const rentMiner = async (userId, cost, powerIncrease) => {
    // userId is Telegram ID
    if (!userId || typeof cost !== 'number' || cost <= 0 || typeof powerIncrease !== 'number' || powerIncrease <= 0) {
        return { success: false, error: 'Invalid request parameters for rentMiner' };
    }
    try {
        // Standardized: pass cost and powerIncrease directly in the payload object
        const response = await callAppwriteProxy('rentMiner', {
            userId: userId.toString(),
            cost: cost,
            powerIncrease: powerIncrease
        });
        // Expect backend to return { success: true, newPrimaryBalance, newMinerPower }
        return response;
    } catch (error) {
        console.error('rentMiner failed via proxy:', error);
        return { success: false, error: error.message || 'Failed to rent miner' };
    }
};

/**
 * Fetches all users referred by a specific user ID.
 */
// updateUserWalletAddress was here

/**
 * Calls the cloud function to verify Telegram join and claim the reward.
 */
export const claimSponsorReward = async (userId, sponsorId) => {
    if (!userId || !sponsorId) {
        throw new Error('User ID and Sponsor ID are required.');
    }

    try {
        const result = await functions.createExecution(
            process.env.REACT_APP_VERIFY_FUNCTION_ID, // Function ID
            JSON.stringify({ userId, sponsorId, action: "claimSponsorReward" }), // Body with action
            false,                                  // Async
            '/',                                    // Path
            'POST',                                 // Method
            { 'Content-Type': 'application/json' }  // Headers
        );

        if (result.status === 'completed') {
            // For sync functions, the response is in `responseBody` as a JSON string.
            try {
                const response = JSON.parse(result.responseBody);
                if (!response.success) {
                    throw new Error(response.message || 'Failed to claim reward.');
                }
                return response; // Return the full object { success, newBalance, claimedSponsorId }
            } catch (e) {
                console.error('Failed to parse function response:', result.responseBody);
                throw new Error('Invalid response from server.');
            }

        } else {
            throw new Error('Function execution failed: ' + (result.stderr || 'Unknown error'));
        }
    } catch (error) {
        console.error('Error in claimSponsorReward:', error);
        throw error; // Re-throw the error for the UI to handle
    }
};

/**
 * Fetches all sponsors from the database.
 */
export const getSponsors = async () => {
    // This action does not require a userId
    try {
        const response = await callAppwriteProxy('getSponsors', {});
        return response.sponsors; // Expect backend to return { success: true, sponsors: documents }
    } catch (error) {
        console.error('getSponsors failed via proxy:', error);
        return [];
    }
};

export const getReferredUsers = async (referrerId) => {
    // referrerId is Telegram ID of the referrer
    if (!referrerId) {
        console.error('getReferredUsers: referrerId is missing');
        return [];
    }
    try {
        const response = await callAppwriteProxy('getReferredUsers', { referrerId: referrerId.toString() });
        // Expect backend to return { success: true, referredUsers: documents }
        return response.referredUsers;
    } catch (error) {
        console.error('getReferredUsers failed via proxy:', error);
        return [];
    }
};

/**
 * Gets the user's daily login information.
 * @param {string} userId The user's Telegram ID.
 * @returns {Promise<object>} The user's daily login information.
 */
export const getDailyLoginInfo = async (userId) => {
    // userId is Telegram ID
    if (!userId) {
        console.error('getDailyLoginInfo: userId is missing');
        return null;
    }
    try {
        const response = await callAppwriteProxy('getDailyLoginInfo', { userId: userId.toString() });
        // Expect backend to return { success: true, loginInfo: { lastLoginDate, currentDay, canClaimToday, loginStreak } }
        return response.loginInfo;
    } catch (error) {
        console.error('getDailyLoginInfo failed via proxy:', error);
        return null;
    }
};

/**
 * Claims the daily login reward and updates the user's streak.
 * @param {string} userId The user's Telegram ID.
 * @returns {Promise<object>} Result of the claim operation.
 */
export const claimDailyLoginReward = async (userId) => {
    // userId is Telegram ID
    if (!userId) {
        console.error('claimDailyLoginReward: userId is missing');
        return { success: false, error: 'User ID is missing' };
    }
    try {
        const response = await callAppwriteProxy('claimDailyLoginReward', { userId: userId.toString() });
        // Expect backend to return { success: true, day, rewardAmount, newBalance, streak }
        // or { success: false, error, nextClaimTime }
        return response;
    } catch (error) {
        console.error('claimDailyLoginReward failed via proxy:', error);
        return { success: false, error: error.message || 'Failed to claim daily reward' };
    }
};